import { Component, OnInit, Inject, signal, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { JJamesCardComponent } from 'shared-lib';
import {
  MasterDeviceAssignmentService,
  MasterDeviceAssignmentDto,
  InfrastructureDto
} from '../../../../api/gen';
import { MasterControllerItemComponent } from './master-controller-item/master-controller-item.component';
import { firstValueFrom } from 'rxjs';

export interface MasterControllerListDialogData {
  infrastructure: InfrastructureDto;
  parkingSpaceId: string;
}

@Component({
  selector: 'app-master-controller-list',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatListModule,
    MatSnackBarModule,
    MatTooltipModule,
    MasterControllerItemComponent
  ],
  templateUrl: './master-controller-list.component.html',
  styleUrl: './master-controller-list.component.scss'
})
export class MasterControllerListComponent implements OnInit {
  infrastructure: InfrastructureDto;
  parkingSpaceId: string;

  assignments = signal<MasterDeviceAssignmentDto[]>([]);
  isLoading = signal(true);
  isDeleting = signal<string | null>(null);

  private masterDeviceAssignmentService = inject(MasterDeviceAssignmentService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  constructor(
    private dialogRef: MatDialogRef<MasterControllerListComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MasterControllerListDialogData
  ) {
    this.infrastructure = data.infrastructure;
    this.parkingSpaceId = data.parkingSpaceId;
  }

  ngOnInit(): void {
    this.loadMasterDeviceAssignments();
  }

  private loadMasterDeviceAssignments(): void {
    this.isLoading.set(true);

    if (!this.parkingSpaceId) {
      this.isLoading.set(false);
      return;
    }

    this.masterDeviceAssignmentService.masterDeviceAssignmentsByParkingSpaceParkingSpaceIdGet(
      this.parkingSpaceId
    ).subscribe({
      next: (result) => {
        if (result) {
          this.assignments.set(result);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading master device assignments:', error);
        this.snackBar.open('Failed to load master controllers', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onAddNew(): void {
    this.dialogRef.close(false);
    this.router.navigate(['/parking-space', this.parkingSpaceId, 'master-controller', 'new']);
  }

  async onEdit(assignment: MasterDeviceAssignmentDto): Promise<void> {
    if (!assignment.masterDeviceId) {
      this.snackBar.open('Cannot edit: Master device ID not found', 'Close', { duration: 3000 });
      return;
    }

    this.dialogRef.close(false);
    this.router.navigate(['/parking-space', this.parkingSpaceId, 'master-controller', assignment.masterDeviceId, 'edit']);
  }

  async onDelete(assignment: MasterDeviceAssignmentDto): Promise<void> {
    if (!assignment.id) return;

    if (!confirm('Are you sure you want to delete this master controller assignment?')) {
      return;
    }

    this.isDeleting.set(assignment.id);

    try {
      await firstValueFrom(this.masterDeviceAssignmentService.masterDeviceAssignmentsIdDelete(assignment.id));
      this.snackBar.open('Master controller assignment deleted successfully', 'Close', { duration: 3000 });
      this.assignments.update(items => items.filter(item => item.id !== assignment.id));
    } catch (error) {
      console.error('Error deleting master controller assignment:', error);
      this.snackBar.open('Failed to delete master controller assignment', 'Close', { duration: 5000 });
    } finally {
      this.isDeleting.set(null);
    }
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  onRefresh(): void {
    this.loadMasterDeviceAssignments();
  }
}
